import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import React from 'react';

import OAuthLoginForm from './components/OAuthLoginForm';
import AuthPinShell from './AuthPinShell';

interface AuthPinCenteredSignupProps {
  t: (key: string) => string;
  error: string | null;
  success: string | null;
  redirecting?: boolean;
  selectedPlan: { planId: string; billing: string } | null;
  onBackToHome: () => void;
  onAuthStarted?: () => void;
  onAuthSuccess: () => void;
  onAuthError: (message: string) => void;
}

const AuthPinCenteredSignup: React.FC<AuthPinCenteredSignupProps> = ({
  t,
  error,
  success,
  redirecting = false,
  selectedPlan,
  onBackToHome,
  onAuthStarted,
  onAuthSuccess,
  onAuthError,
}) => {
  const goLogin = () => {
    const qs = new URLSearchParams(window.location.search).toString();
    window.location.href = qs ? `/login?${qs}` : '/login';
  };

  return (
    <AuthPinShell
      t={t}
      onBackToHome={onBackToHome}
      redirecting={redirecting}
      layout="form-right"
      proofMode="pin"
      eyebrowKey="auth.signupLeftEyebrow"
      titleBeforeKey="auth.signupLeftTitleBefore"
      titleAccentKey="auth.signupLeftTitleAccent"
      subtitleKey="auth.signupLeftSubtitle"
      footerStatKey="auth.pinFooterStat"
      cardHeadingKey="auth.signUpWithHeading"
      cardSubtitleKey="auth.oauthSubtitle"
      formPanelTopKey="auth.signupFormPanelTop"
      formPanelBottomKey="auth.signupFormPanelBottom"
      cardNote={
        <p className="auth-pin-login__body auth-pin-login__card-note">
          {t('auth.legalPrefix')}{' '}
          <button
            type="button"
            onClick={() => window.open('/terms-of-service', '_blank', 'noopener,noreferrer')}
            className="auth-pin-login__legal-link"
          >
            {t('footer.termsAndConditions')}
          </button>{' '}
          {t('auth.legalAnd')}{' '}
          <button
            type="button"
            onClick={() => window.open('/privacy-policy', '_blank', 'noopener,noreferrer')}
            className="auth-pin-login__legal-link"
          >
            {t('footer.privacyPolicy')}
          </button>
          .
        </p>
      }
      cardFooter={
        <p className="auth-page__switch">
          {t('auth.alreadyHaveAccount')}{' '}
          <button type="button" onClick={goLogin} className="auth-page__switch-btn">
            {t('auth.logIn')}
          </button>
        </p>
      }
    >
      {selectedPlan && (
        <div className="auth-page__plan-banner">
          <p className="auth-page__plan-banner-label">{t('auth.selectedPlan')}</p>
          <p className="auth-page__plan-banner-value">
            {selectedPlan.planId} —{' '}
            {selectedPlan.billing === 'monthly' ? t('views.pricing.monthly') : t('views.pricing.annual')}
          </p>
        </div>
      )}

      <AnimatePresence mode="wait">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="auth-page__alert auth-page__alert--error"
            role="alert"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{error}</span>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="auth-page__alert auth-page__alert--success"
          >
            <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <OAuthLoginForm
        authMode="signup"
        onAuthStarted={onAuthStarted}
        onSuccess={onAuthSuccess}
        onError={onAuthError}
        variant="pin-light"
        oauthProviders={['google', 'linkedin']}
      />
    </AuthPinShell>
  );
};

export default AuthPinCenteredSignup;
